import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Lesson {
  id: string;
  title: string;
  description: string;
  type: 'vocabulary' | 'grammar' | 'speaking' | 'listening' | 'reading' | 'writing';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration: number; // in minutes
  points: number;
  isCompleted: boolean;
  progress: number; // 0-100
  exercises: Exercise[];
  thumbnail?: string;
}

export interface Exercise {
  id: string;
  type: 'multiple-choice' | 'fill-blank' | 'speaking' | 'listening' | 'matching';
  question: string;
  options?: string[];
  correctAnswer: string | string[];
  explanation?: string;
  audioUrl?: string;
  imageUrl?: string;
  isCompleted: boolean;
  userAnswer?: string | string[];
}

export interface LessonProgress {
  lessonId: string;
  completedExercises: string[];
  score: number;
  timeSpent: number;
  attempts: number;
  lastAccessed: string;
}

interface LessonsState {
  lessons: Lesson[];
  currentLesson: Lesson | null;
  currentExercise: Exercise | null;
  progress: LessonProgress[];
  recommendedLessons: Lesson[];
  bookmarkedLessons: Lesson[];
  categories: string[];
  types: string[];
  difficulties: string[];
  stats: {
    totalLessons: number;
    completedLessons: number;
    totalPoints: number;
    averageScore: number;
    totalStudyTime: number;
    streakDays: number;
  } | null;
  pagination: {
    page: number;
    totalPages: number;
    total: number;
  };
  isLoading: boolean;
  error: string | null;
  filter: {
    type: string;
    difficulty: string;
    completed: boolean | null;
  };
}

const initialState: LessonsState = {
  lessons: [],
  currentLesson: null,
  currentExercise: null,
  progress: [],
  recommendedLessons: [],
  bookmarkedLessons: [],
  categories: [],
  types: [],
  difficulties: [],
  stats: null,
  pagination: {
    page: 1,
    totalPages: 1,
    total: 0,
  },
  isLoading: false,
  error: null,
  filter: {
    type: 'all',
    difficulty: 'all',
    completed: null,
  },
};

const lessonsSlice = createSlice({
  name: 'lessons',
  initialState,
  reducers: {
    // Saga request actions
    fetchLessonsRequest: (state: LessonsState, action: PayloadAction<{ filters?: any; pagination?: any }>) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchLessonsSuccess: (
      state,
      action: PayloadAction<{ lessons: Lesson[]; total: number; page: number; totalPages: number }>,
    ) => {
      state.isLoading = false;
      state.lessons = action.payload.lessons;
      state.pagination = {
        page: action.payload.page,
        totalPages: action.payload.totalPages,
        total: action.payload.total,
      };
    },
    fetchLessonsFailure: (state: LessonsState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    fetchLessonByIdRequest: (state: LessonsState, action: PayloadAction<string>) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchLessonByIdSuccess: (state: LessonsState, action: PayloadAction<Lesson>) => {
      state.isLoading = false;
      state.currentLesson = action.payload;
    },
    fetchLessonByIdFailure: (state: LessonsState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    fetchLessonProgressRequest: (state: LessonsState, action: PayloadAction<string | undefined>) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchLessonProgressSuccess: (state: LessonsState, action: PayloadAction<LessonProgress[]>) => {
      state.isLoading = false;
      state.progress = action.payload;
    },
    fetchLessonProgressFailure: (state: LessonsState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    submitExerciseRequest: (state: LessonsState, action: PayloadAction<any>) => {
      state.isLoading = true;
      state.error = null;
    },
    submitExerciseSuccess: (state: LessonsState, action: PayloadAction<any>) => {
      state.isLoading = false;
      // Handle exercise result
    },
    submitExerciseFailure: (state: LessonsState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    completeLessonRequest: (state: LessonsState, action: PayloadAction<any>) => {
      state.isLoading = true;
      state.error = null;
    },
    completeLessonSuccess: (state: LessonsState, action: PayloadAction<LessonProgress>) => {
      state.isLoading = false;
      const existingIndex = state.progress.findIndex((p) => p.lessonId === action.payload.lessonId);
      if (existingIndex >= 0) {
        state.progress[existingIndex] = action.payload;
      } else {
        state.progress.push(action.payload);
      }
      // Mark lesson as completed
      const lesson = state.lessons.find((l) => l.id === action.payload.lessonId);
      if (lesson) {
        lesson.isCompleted = true;
        lesson.progress = 100;
      }
    },
    completeLessonFailure: (state: LessonsState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    fetchLessonStatsRequest: (state: LessonsState) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchLessonStatsSuccess: (state: LessonsState, action: PayloadAction<typeof initialState.stats>) => {
      state.isLoading = false;
      state.stats = action.payload;
    },
    fetchLessonStatsFailure: (state: LessonsState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    fetchRecommendedLessonsRequest: (state: LessonsState) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchRecommendedLessonsSuccess: (state: LessonsState, action: PayloadAction<Lesson[]>) => {
      state.isLoading = false;
      state.recommendedLessons = action.payload;
    },
    fetchRecommendedLessonsFailure: (state: LessonsState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    fetchBookmarkedLessonsRequest: (state: LessonsState) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchBookmarkedLessonsSuccess: (state: LessonsState, action: PayloadAction<Lesson[]>) => {
      state.isLoading = false;
      state.bookmarkedLessons = action.payload;
    },
    fetchBookmarkedLessonsFailure: (state: LessonsState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    fetchLessonCategoriesRequest: (state: LessonsState) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchLessonCategoriesSuccess: (state: LessonsState, action: PayloadAction<string[]>) => {
      state.isLoading = false;
      state.categories = action.payload;
    },
    fetchLessonCategoriesFailure: (state: LessonsState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    bookmarkLessonRequest: (state: LessonsState, action: PayloadAction<string>) => {
      state.error = null;
    },
    bookmarkLessonSuccess: (state: LessonsState, action: PayloadAction<string>) => {
      // Add to bookmarked lessons if not already there
      const lesson = state.lessons.find((l) => l.id === action.payload);
      if (lesson && !state.bookmarkedLessons.find((bl) => bl.id === action.payload)) {
        state.bookmarkedLessons.push(lesson);
      }
    },
    bookmarkLessonFailure: (state: LessonsState, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Local state actions
    setLessons: (state: LessonsState, action: PayloadAction<Lesson[]>) => {
      state.lessons = action.payload;
    },
    setCurrentLesson: (state: LessonsState, action: PayloadAction<Lesson>) => {
      state.currentLesson = action.payload;
    },
    setCurrentExercise: (state: LessonsState, action: PayloadAction<Exercise>) => {
      state.currentExercise = action.payload;
    },
    updateLessonProgress: (state: LessonsState, action: PayloadAction<LessonProgress>) => {
      const existingIndex = state.progress.findIndex((p) => p.lessonId === action.payload.lessonId);
      if (existingIndex >= 0) {
        state.progress[existingIndex] = action.payload;
      } else {
        state.progress.push(action.payload);
      }
    },
    completeExercise: (state: LessonsState, action: PayloadAction<{ exerciseId: string; userAnswer: string | string[] }>) => {
      if (state.currentLesson) {
        const exercise = state.currentLesson.exercises.find((e) => e.id === action.payload.exerciseId);
        if (exercise) {
          exercise.isCompleted = true;
          exercise.userAnswer = action.payload.userAnswer;
        }
      }
    },
    completeLesson: (state: LessonsState, action: PayloadAction<string>) => {
      const lesson = state.lessons.find((l) => l.id === action.payload);
      if (lesson) {
        lesson.isCompleted = true;
        lesson.progress = 100;
      }
    },
    setFilter: (state: LessonsState, action: PayloadAction<Partial<typeof initialState.filter>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    setLoading: (state: LessonsState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state: LessonsState, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  // Saga actions
  fetchLessonsRequest,
  fetchLessonsSuccess,
  fetchLessonsFailure,
  fetchLessonByIdRequest,
  fetchLessonByIdSuccess,
  fetchLessonByIdFailure,
  fetchLessonProgressRequest,
  fetchLessonProgressSuccess,
  fetchLessonProgressFailure,
  submitExerciseRequest,
  submitExerciseSuccess,
  submitExerciseFailure,
  completeLessonRequest,
  completeLessonSuccess,
  completeLessonFailure,
  fetchLessonStatsRequest,
  fetchLessonStatsSuccess,
  fetchLessonStatsFailure,
  fetchRecommendedLessonsRequest,
  fetchRecommendedLessonsSuccess,
  fetchRecommendedLessonsFailure,
  fetchBookmarkedLessonsRequest,
  fetchBookmarkedLessonsSuccess,
  fetchBookmarkedLessonsFailure,
  fetchLessonCategoriesRequest,
  fetchLessonCategoriesSuccess,
  fetchLessonCategoriesFailure,
  bookmarkLessonRequest,
  bookmarkLessonSuccess,
  bookmarkLessonFailure,

  // Local actions
  setLessons,
  setCurrentLesson,
  setCurrentExercise,
  updateLessonProgress,
  completeExercise,
  completeLesson,
  setFilter,
  setLoading,
  setError,
} = lessonsSlice.actions;

export default lessonsSlice.reducer;
